import { Box, Card, CardBody, Divider, HStack, SimpleGrid, Spinner, Stack, Text, VStack } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { ReactNode } from "react";
import { adminGetAnalysisChat } from "../../../api";
import AdminOnly from "../../../components/Auth/AdminOnly";
import { colors } from "../../../styles";
import { IAdminAnalysisChat } from "../../../types";

const RETENTION_TYPES: { key: keyof IAdminAnalysisChat["retension"]; label: string; color: string }[] = [
    { key: "owner", label: "개국약사", color: colors.blue },
    { key: "pharmacist", label: "약사", color: colors.covalt },
    { key: "student", label: "약대생", color: colors.teal },
];

function safeNumber(value: number | undefined) {
    return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function formatCount(value: number) {
    return Math.round(safeNumber(value)).toLocaleString("ko-KR");
}

function formatDays(value: number) {
    return safeNumber(value).toLocaleString("ko-KR", { maximumFractionDigits: 1 });
}

function isAnalysisChat(data: IAdminAnalysisChat | undefined): data is IAdminAnalysisChat {
    return Boolean(data?.visit && data.active && data.heavy && data.retension);
}

function AnalysisCard({ children }: { children: ReactNode }) {
    return (
        <Card
            width={"100%"}
            height={"100%"}
            border={`0.5px solid ${colors.g4}`}
            borderRadius={10}
            shadow={"md"}
            backgroundColor={colors.white}
        >
            <CardBody>{children}</CardBody>
        </Card>
    );
}

function SectionHeading({ title, description }: { title: string; description: string }) {
    return (
        <Stack spacing={1}>
            <Text fontSize={18} fontWeight={700} color={colors.black}>{title}</Text>
            <Text fontSize={13} fontWeight={400} color={colors.g2}>{description}</Text>
        </Stack>
    );
}

function StatCard({ label, value }: { label: string; value: string }) {
    return (
        <AnalysisCard>
            <VStack width={"100%"} minH={"108px"} justifyContent={"center"} alignItems={"flex-start"} spacing={3}>
                <Text fontSize={16} fontWeight={500} color={colors.black} minH={"48px"} lineHeight={"24px"}>{label}</Text>
                <Text fontSize={48} fontWeight={400} color={colors.black} lineHeight={"1"} sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {value}
                </Text>
            </VStack>
        </AnalysisCard>
    );
}

function RetentionCard({ retension }: { retension: IAdminAnalysisChat["retension"] | undefined }) {
    const rows = RETENTION_TYPES.map((type) => ({
        ...type,
        value: safeNumber(retension?.[type.key]),
    }));

    return (
        <AnalysisCard>
            <SimpleGrid columns={{ base: 1, md: 3 }} gap={6}>
                {rows.map((row) => (
                    <VStack key={row.key} align={"flex-start"} spacing={2}>
                        <HStack spacing={2}>
                            <Box boxSize={"8px"} borderRadius={"full"} backgroundColor={row.color} />
                            <Text fontSize={13} fontWeight={500} color={colors.g2}>{row.label}</Text>
                        </HStack>
                        <HStack align={"baseline"} spacing={1}>
                            <Text fontSize={32} fontWeight={400} color={colors.black} lineHeight={"1"} sx={{ fontVariantNumeric: "tabular-nums" }}>
                                {formatDays(row.value)}
                            </Text>
                            <Text fontSize={14} fontWeight={400} color={colors.g2}>일</Text>
                        </HStack>
                    </VStack>
                ))}
            </SimpleGrid>
        </AnalysisCard>
    );
}

export default function Chatting() {
    const { data, isLoading, isError } = useQuery<IAdminAnalysisChat>({
        queryKey: ["adminGetAnalysisChat"],
        queryFn: adminGetAnalysisChat,
    });

    const analysis = isAnalysisChat(data) ? data : undefined;

    return (
        <AdminOnly>
            <Stack width={"100%"} padding={10} spacing={8}>
                <HStack justifyContent={"space-between"} alignItems={"center"}>
                    <Text fontSize={28} fontWeight={700} color={colors.black}>채팅 분석</Text>
                </HStack>

                {isLoading ? (
                    <Spinner color={colors.blue} />
                ) : isError || !analysis ? (
                    <Text fontSize={14} fontWeight={400} color={colors.g2}>채팅 분석 데이터를 불러오지 못했습니다.</Text>
                ) : (
                    <>
                        <Stack spacing={4}>
                            <SectionHeading
                                title={"방문"}
                                description={"채팅에 방문한 횟수입니다."}
                            />
                            <SimpleGrid columns={{ base: 1, lg: 3 }} gap={5}>
                                <StatCard label={"방문 수"} value={formatCount(analysis.visit.total)} />
                            </SimpleGrid>
                        </Stack>

                        <Divider />

                        <Stack spacing={4}>
                            <SectionHeading
                                title={"활성 사용자"}
                                description={"최근 2주 안에 각 행동을 한 사용자 수입니다."}
                            />
                            <SimpleGrid columns={{ base: 1, lg: 3 }} gap={5}>
                                <StatCard label={"방문 사용자"} value={formatCount(analysis.active.visit)} />
                                <StatCard label={"글, 댓글, 대댓글 사용자"} value={formatCount(analysis.active.write)} />
                                <StatCard label={"좋아요, 저장됨 사용자"} value={formatCount(analysis.active.action)} />
                            </SimpleGrid>
                        </Stack>

                        <Divider />

                        <Stack spacing={4}>
                            <SectionHeading
                                title={"헤비 사용자"}
                                description={"일주일 동안 두 번 이상 채팅에 방문한 사용자입니다."}
                            />
                            <SimpleGrid columns={{ base: 1, lg: 3 }} gap={5}>
                                <StatCard label={"일주일 2회 이상 방문 사용자"} value={formatCount(analysis.heavy.visit)} />
                            </SimpleGrid>
                        </Stack>

                        <Divider />

                        <Stack spacing={4}>
                            <SectionHeading
                                title={"재방문 주기"}
                                description={"다시 방문하기까지 걸리는 평균 간격입니다. 짧을수록 더 자주 돌아옵니다."}
                            />
                            <RetentionCard retension={analysis.retension} />
                        </Stack>
                    </>
                )}
            </Stack>
        </AdminOnly>
    );
}
